import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { HasPermissionDirective } from '../../../utils/has-permission.directive';
import { ConfirmAlertComponent } from '../../shared/confirm-alert/confirm-alert.component';
import { FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';
import { FullSpinnerService } from '../../../services/full-spinner.service';
import { lastValueFrom, Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { Organization } from '../../../models/Organization';
import { OrganizationService } from '../../../services/organization.service';
import { AppConstants } from '../../../utils/AppConstants';
import { License } from '../../../models/License';
import { LicenseService } from '../../../services/license.service';
import { LicenseSearchCriteria } from '../../../models/searchCriteria/LicenseSearchCriteria';
import { AlertService } from '../../shared/alert/alert.service';
import { CityAutocompleteComponent } from '../../shared/city-autocomplete/city-autocomplete.component';
import { Bridge } from '../../../models/Bridge';
import { BridgeService } from '../../../services/bridge.service';

@Component({
  selector: 'app-organization-detail',
  standalone: true,
  imports: [
    CommonModule,
    HasPermissionDirective,
    ConfirmAlertComponent,
    FormsModule,
    ReactiveFormsModule,
    CityAutocompleteComponent,
  ],
  templateUrl: './organization-detail.component.html',
  styleUrl: './organization-detail.component.scss',
})
export class OrganizationDetailComponent implements OnInit, OnDestroy {
  paramSubscription!: Subscription;
  showConfirmationModal: boolean = false;
  confirmationMessage: string = '';

  id!: number;
  organization: Organization = {};
  detail_title: string = 'Nuovo ente';

  licenses: License[] = [];
  licenseName: string = '';
  licensePatternValidation = '';

  bridges: Bridge[] = [];

  constructor(
    private organizationService: OrganizationService,
    private licenseServices: LicenseService,
    private bridgeService: BridgeService,
    private alertService: AlertService,
    private fullSpinnerService: FullSpinnerService,
    private activatedRoute: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadBridges();
    this.paramSubscription = this.activatedRoute.params.subscribe(
      (parameter) => {
        this.id = parameter['id'];
        if (this.id > 0) {
          this.fullSpinnerService.spinner('');
          this.organizationService.get(this.id).subscribe({
            next: (res) => {
              this.organization = res;
              this.detail_title = res.name!;
              this.ensureCurrentBridgeInList();
              if (res.licenseId) {
                let license = this.getLicenseById(res.licenseId);
                license.then((r) => {
                  this.licenseName = r.name!;
                  this.licensePatternValidation = '^' + this.licenseName + '$';
                  this.fullSpinnerService.spinner(undefined);
                });
              } else {
                this.fullSpinnerService.spinner(undefined);
              }
            },
            complete: () => {},
            error: () => {
              this.fullSpinnerService.spinner(undefined);
            },
          });
        }
      }
    );
  }

  private loadBridges(): void {
    this.bridgeService.findAll().subscribe({
      next: (res) => {
        this.bridges = res;
        this.ensureCurrentBridgeInList();
      },
      error: () => {},
    });
  }

  // In modifica, se il bridge associato è disabilitato non è nella lista (solo enabled):
  // lo aggiungo comunque così resta selezionato.
  private ensureCurrentBridgeInList(): void {
    const id = this.organization.bridgeId;
    if (id != null && !this.bridges.some((b) => b.id === id)) {
      this.bridges = [...this.bridges, { id, name: this.organization.bridgeName }];
    }
  }

  onSubmit(loginForm: NgForm): void {
    if (loginForm.valid) {
      this.fullSpinnerService.spinner('');
      if (this.licenseName == '') this.organization.licenseId = undefined;

      this.organizationService.save(this.organization).subscribe({
        next: (_) => {
          this.alertService.addErrors(
            AppConstants.titleSuccessMessage,
            [AppConstants.successMessage],
            true
          );
          this.router.navigate(['organizations']);
        },
        complete: () => {
          this.fullSpinnerService.spinner(undefined);
        },
        error: () => {
          this.alertService.addErrors(
            AppConstants.titleErrorMessage,
            [AppConstants.errorMessage],
            true
          );
          this.fullSpinnerService.spinner(undefined);
        },
      });
    }
  }

  private async getLicenseById(id: number) {
    let license$ = this.licenseServices.get(id);
    return await lastValueFrom(license$);
  }

  onLicenseKeyUp(event: any) {
    //Fake Validation
    this.licensePatternValidation =
      '@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@@';
    let value = event.target.value;
    if (value.length > 3) {
      let sc: LicenseSearchCriteria = {
        name: value,
        type: ['LITE', 'TRANSACTION'],
        pageNumber: 1,
        pageSize: 30,
      };
      this.licenseServices.find(sc).subscribe({
        next: (res) => {
          this.licenses = res.items;
        },
        complete: () => {
          this.fullSpinnerService.spinner(undefined);
        },
        error: () => {
          this.fullSpinnerService.spinner(undefined);
        },
      });
    }
  }

  selectLicense(license: License) {
    this.organization.licenseId = license.id!;
    this.licenseName = license.name!;
    this.licensePatternValidation = '^' + this.licenseName + '$';
    this.licenses = [];
  }

  back() {
    this.router.navigate(['organizations']);
  }

  confirmDelete() {
    this.showConfirmationModal = true;
    this.confirmationMessage =
      "Sicuro di voler eliminare l'ente " + this.organization.name + ' ?';
  }

  cancelDelete() {
    this.showConfirmationModal = false;
  }

  delete() {
    this.fullSpinnerService.spinner('');
    this.organizationService.delete(this.organization.id!).subscribe({
      next: (res) => {
        this.alertService.addErrors(
          AppConstants.titleDeleteSuccessMessage,
          [AppConstants.successMessage],
          true
        );
        this.router.navigate(['organizations']);
      },
      complete: () => {
        this.fullSpinnerService.spinner(undefined);
      },
      error: () => {
        this.alertService.addErrors(
          AppConstants.titleErrorMessage,
          [AppConstants.errorMessage],
          true
        );
        this.fullSpinnerService.spinner(undefined);
      },
    });
  }

  ngOnDestroy(): void {
    this.paramSubscription.unsubscribe();
  }
}
